import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { z } from 'zod';
import { PERMISSION_RIDE_TAG } from '../../Services/PermissionsService';
import { requirePermission } from '../../router/RouteGuardsService';
import RideTagForm from '../../components/Codebooks/RideTagForm';
import { RIDE_TAG_TABLE_KEY } from '../../components/Codebooks/constants';
import {
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../components/CmdTable/CmdTableState';
import { getForm } from '../../components/CmdTable/CmdTableService';
import { TABLE_CODEBOOKS_RIDE } from '../../Services/TableService';
import { selectedCustomersAtom } from '../../store/jotai/customers';
import { URL_CODEBOOKS } from '../../router/constants';

const editRideTagSearchSchema = z.object({
  customerId: z.coerce.number().optional(),
});

export const Route = createFileRoute('/_authenticated/system_settings/ride-tag/edit/$id')({
  validateSearch: editRideTagSearchSchema,
  beforeLoad: async ({ context }) => {
    await requirePermission(PERMISSION_RIDE_TAG, ['update'], '/unauthorized', context);
    return {
      ...context,
      documentTitle: {
        main: 'table.detail',
        suffix: 'tab.ride-tag',
      },
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: EditRideTagRoute,
});

function EditRideTagRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedCustomers = useAtomValue(selectedCustomersAtom);
  const filterForRequest = useAtomValue(filterForRequestAtomFamily(RIDE_TAG_TABLE_KEY));
  const sortFilter = useAtomValue(sortFilterAtomFamily(RIDE_TAG_TABLE_KEY));
  const tagId = Number(id);
  const customerId = search.customerId ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ['rideTagForm', tagId, customerId],
    queryFn: () => getForm(TABLE_CODEBOOKS_RIDE, tagId, customerId ?? undefined),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleClose = React.useCallback(() => {
    navigate({ to: `${URL_CODEBOOKS}/${TABLE_CODEBOOKS_RIDE}` });
  }, [navigate]);

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [RIDE_TAG_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [filterForRequest, queryClient, selectedCustomers, sortFilter]);

  if (isLoading || !data?.length) {
    return null;
  }

  return (
    <RideTagForm
      id={tagId}
      customerId={customerId}
      data={data}
      updateTable={updateTable}
      handleClose={handleClose}
    />
  );
}
